import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { SUBSIDIARIES } from "@/data/ekipData";
import { Home as HomeIcon, Zap, Cpu, Wallet, TrendingUp } from "lucide-react";

const ICONS = { home: HomeIcon, zap: Zap, cpu: Cpu, wallet: Wallet, "trending-up": TrendingUp };

// Positions (radial around center)
const NODE_POSITIONS = {
  kidh:         { x: 50, y: 12 },
  "kip-energy": { x: 88, y: 40 },
  ekiptech:     { x: 74, y: 85 },
  kipay:        { x: 26, y: 85 },
  "ekip-capital": { x: 12, y: 40 },
};

// Synergies (edges between nodes)
const SYNERGIES = [
  { from: "kidh", to: "kip-energy", key: "kidh-kip" },
  { from: "kidh", to: "ekiptech", key: "kidh-tech" },
  { from: "kidh", to: "kipay", key: "kidh-pay" },
  { from: "kip-energy", to: "ekiptech", key: "kip-tech" },
  { from: "kip-energy", to: "kipay", key: "kip-pay" },
  { from: "ekiptech", to: "kipay", key: "tech-pay" },
];

export default function EcosystemSchema({ variant = "full", darkBg = false }) {
  const { t, lang } = useLang();
  const [active, setActive] = useState(null);

  const activeSyns = useMemo(() => {
    if (!active) return [];
    return SYNERGIES.filter((s) => s.from === active || s.to === active);
  }, [active]);

  const activeSub = SUBSIDIARIES.find((s) => s.id === active);
  const details = active
    ? t.ecosysteme.synergies.items.filter(
        (it) =>
          it.from.toLowerCase().replace(/\s+/g, "-") === active ||
          it.to.toLowerCase().replace(/\s+/g, "-") === active
      )
    : [];

  return (
    <div className="relative w-full">
      <div className="relative aspect-square max-w-[720px] mx-auto">
        {/* SVG connections */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="none"
        >
          {/* Rings */}
          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(196,154,60,0.08)" strokeWidth="0.15" />
          <circle cx="50" cy="50" r="34" fill="none" stroke="rgba(196,154,60,0.12)" strokeWidth="0.15" />
          <circle cx="50" cy="50" r="24" fill="none" stroke="rgba(196,154,60,0.18)" strokeWidth="0.15" />

          {/* Center to nodes */}
          {SUBSIDIARIES.map((s) => {
            const p = NODE_POSITIONS[s.id];
            return (
              <line
                key={`center-${s.id}`}
                x1="50"
                y1="50"
                x2={p.x}
                y2={p.y}
                stroke={active === s.id ? s.accent : "rgba(196,154,60,0.35)"}
                strokeWidth={active === s.id ? "0.45" : "0.2"}
                strokeDasharray={active === s.id ? "0" : "0.6 0.6"}
                style={{ transition: "all 0.5s ease" }}
              />
            );
          })}

          {/* Synergies */}
          {SYNERGIES.map((s) => {
            const p1 = NODE_POSITIONS[s.from];
            const p2 = NODE_POSITIONS[s.to];
            const isActive = active && (s.from === active || s.to === active);
            return (
              <line
                key={s.key}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke={isActive ? "#E8611A" : "rgba(232,97,26,0.12)"}
                strokeWidth={isActive ? "0.5" : "0.2"}
                strokeDasharray={isActive ? "0" : "0.4 0.4"}
                style={{ transition: "all 0.5s ease" }}
              />
            );
          })}
        </svg>

        {/* Center hub */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
          data-testid="ecosystem-center"
        >
          <div className="relative w-28 h-28 md:w-36 md:h-36 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-ekip-navy border-2 border-ekip-copper pulse-glow"
              style={{ transform: "rotate(45deg)" }}
            />
            <div className="relative z-10 flex flex-col items-center gap-1">
              <span className="font-display font-bold text-white text-sm md:text-base tracking-tight">
                EKIP
              </span>
              <span className="text-[8px] md:text-[9px] uppercase tracking-[0.28em] text-ekip-copper">
                {t.ecosysteme.schema.holdingLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Subsidiary nodes */}
        {SUBSIDIARIES.map((s, idx) => {
          const p = NODE_POSITIONS[s.id];
          const Icon = ICONS[s.icon] || HomeIcon;
          const isActive = active === s.id;
          return (
            <motion.button
              key={s.id}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.08 }}
              onClick={() => setActive(isActive ? null : s.id)}
              data-testid={`ecosystem-node-${s.id}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 z-20 group cursor-pointer transition-all duration-500 ${
                active && !isActive ? "opacity-40" : "opacity-100"
              }`}
              style={{ left: `${p.x}%`, top: `${p.y}%` }}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="relative w-16 h-16 md:w-20 md:h-20 flex items-center justify-center">
                  <div
                    className={`absolute inset-0 border-2 transition-all ${
                      isActive ? "scale-110" : "group-hover:scale-105"
                    }`}
                    style={{
                      transform: "rotate(45deg)",
                      background: isActive ? s.accent : "#FAFAF8",
                      borderColor: s.accent,
                    }}
                  />
                  <Icon
                    size={22}
                    strokeWidth={1.5}
                    color={isActive ? "#fff" : s.accent}
                    className="relative z-10"
                  />
                </div>
                <span
                  className="font-display font-semibold text-[11px] md:text-sm text-center whitespace-nowrap px-2 py-0.5"
                  style={{
                    color: isActive ? s.accent : darkBg ? "#FFFFFF" : "#0C2340",
                    background: darkBg && !isActive ? "rgba(10, 28, 51, 0.6)" : "transparent",
                  }}
                >
                  {s.name}
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Details panel */}
      <AnimatePresence mode="wait">
        {activeSub && (
          <motion.div
            key={activeSub.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            data-testid="ecosystem-detail"
            className="mt-10 md:mt-14 bg-white border-l-4 p-6 md:p-10 shadow-sm"
            style={{ borderLeftColor: activeSub.accent }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-4">
              <div>
                <span
                  className="text-[10px] uppercase tracking-[0.3em] font-medium"
                  style={{ color: activeSub.accent }}
                >
                  {activeSub.fullName}
                </span>
                <h3 className="mt-2 font-display font-semibold text-3xl md:text-4xl text-ekip-navy">
                  {activeSub.name}
                </h3>
              </div>
              <p className="font-display italic text-lg md:text-xl text-ekip-graphite max-w-md">
                « {activeSub.tagline[lang]} »
              </p>
            </div>
            <p className="text-ekip-slate leading-relaxed font-light max-w-3xl">
              {activeSub.description[lang]}
            </p>
            {details.length > 0 && (
              <div className="mt-8">
                <span className="text-[10px] uppercase tracking-[0.3em] text-ekip-copper font-medium">
                  {t.ecosysteme.synergies.title}
                </span>
                <ul className="mt-4 space-y-2.5">
                  {details.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <span className="diamond mt-1.5" style={{ background: activeSub.accent }} />
                      <span>
                        <span className="font-medium text-ekip-navy">{d.from} × {d.to}</span>
                        <span className="text-ekip-graphite"> — {d.desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
